import { useRef } from 'react'

import { Wizard, WizardStep } from '@skytech/ui'
import { Container } from '@skytech/ui/Container'
import { getMaxDate, getIsDateInRange } from '@skytech/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { FormikProps } from 'formik'
import { Form, Formik } from 'formik'
import { useNavigate } from 'react-router-dom'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { SUB_SERVICE_ORDER_STATUS } from '@/modules/subscriptions/constants'
import terminationResources from '@/modules/subscriptions/termination/resources'
import { APP_ROUTES } from '@/util/app-routes'

import queries from '../queries'
import type { TerminationType } from '../types'

import { NewTerminationConfirm } from './NewTerminationConfirm'
import { NewTerminationDetails } from './NewTerminationDetails'
import { NewTerminationType } from './NewTerminationType'

type FormFields = {
  terminationType?: TerminationType
  comment: string
  terminationDate: string
  costObjectId: number | null
}

export const NewTerminationForm = () => {
  const navigate = useNavigate()
  const translate = useTranslation()
  const queryClient = useQueryClient()
  const { setIsWizardMode } = useAppContext()
  const formRef = useRef<FormikProps<FormFields> | null>(null)

  const newTerminationMutation = useMutation(terminationResources.post, {
    onSuccess() {
      queryClient.invalidateQueries(queries.terminationList(), { refetchType: 'all' })
      navigate(
        `${APP_ROUTES.SUBSCRIPTIONS_TERMINATION_LIST}?status=${SUB_SERVICE_ORDER_STATUS.NEW}`
      )
    },
  })

  function getIsTerminationDateValid(terminationDate: string) {
    if (!terminationDate) return false
    return getIsDateInRange({
      minDate: new Date(),
      maxDate: getMaxDate(12),
      options: {
        date: new Date(terminationDate),
      },
    })
  }

  const onComplete = (values: FormFields) => {
    if (!formRef.current?.dirty) return
    const { terminationType, terminationDate, costObjectId, comment } = values

    terminationType &&
      costObjectId &&
      newTerminationMutation.mutate({ terminationType, costObjectId, terminationDate, comment })
  }

  return (
    <Container onSwipedLeft={() => navigate(APP_ROUTES.HOME)}>
      <Container.Header>
        <Translation id="New Termination" />
      </Container.Header>

      <Formik<FormFields>
        initialValues={{
          costObjectId: null,
          terminationType: undefined,
          terminationDate: '',
          comment: '',
        }}
        validate={values => {
          const errors: Partial<Record<keyof FormFields, string>> = {}
          if (!values.costObjectId) {
            errors.costObjectId = translate('This field is mandatory')
          }
          if (!values.terminationDate) {
            errors.terminationDate = translate('This field is mandatory')
          }
          if (values.terminationDate && !getIsTerminationDateValid(values.terminationDate)) {
            errors.terminationDate = translate(
              'The provided date must fall between today and 1 year after today'
            )
          }
          if (!values.terminationType) {
            errors.terminationType = translate('This field is mandatory')
          }

          return errors
        }}
        innerRef={formRef}
        onSubmit={formValues => onComplete(formValues)}
      >
        {({ values }) => {
          const isTypeValid = values.terminationType !== undefined
          const isTerminationDetailsValid = Boolean(
            values.costObjectId && getIsTerminationDateValid(values.terminationDate)
          )

          return (
            <Form>
              <Wizard
                isBigNavigationMarginTop
                skipButtonTextDefault={<Translation id="Skip" />}
                nextButtonTextDefault={<Translation id="Next Step" />}
                previousButtonTextDefault={<Translation id="Previous Step" />}
                isLoading={newTerminationMutation.isLoading}
                onIsWizardModeSet={setIsWizardMode}
                onComplete={() => formRef.current?.submitForm()}
              >
                <WizardStep isValid={isTerminationDetailsValid} name={<Translation id="Details" />}>
                  <NewTerminationDetails />
                </WizardStep>

                <WizardStep isValid={isTypeValid} name={<Translation id="Type" />}>
                  <NewTerminationType costObjectId={values.costObjectId} />
                </WizardStep>

                <WizardStep
                  nextButtonText={<Translation id="Complete" />}
                  isValid={true}
                  name={<Translation id="Confirm" />}
                >
                  <NewTerminationConfirm />
                </WizardStep>
              </Wizard>
            </Form>
          )
        }}
      </Formik>
    </Container>
  )
}
